import {TicketingStatusPillVariant} from '@Components/event-ticketing/components/ticketing-status-pill/ticketing-status-pill.types';
import type {TicketingStatusPillProps} from '@Components/event-ticketing/components/ticketing-status-pill/ticketing-status-pill.types';
import type {DecodedCheckInQr, TicketingAttendeeExportFormat, TicketingDashboardState, TicketingOrderSummary, TicketingOrdersListLazyData, TicketingPlanLimit, TicketingQuestionAnswer, TicketingStats, TicketingStatusFilterValue} from '@Components/event-ticketing/event-ticketing.types';
import {OPEN_CHECK_IN_VIEW_QUERY_PARAM, OPEN_SCANNER_QUERY_PARAM, PaymentStatus, OrderAttendanceStatus, TicketingPlanLimitState, TicketingRefundStatus} from '@Components/event-ticketing/event-ticketing.types';
import {formatFeePercent} from '@Components/event-wizard/event-wizard-ticketing/ticketing-fees.helper';
import type {EventHashedID, EventVO} from '@Components/events/events.types';
import {openPremiumPricingModal} from '@Components/modals/premium-pricing-modal/premium-pricing-modal.library';
import {isPastEvent} from '@Libraries/events.library';
import {isUserPremium, isUserTrial} from '@Libraries/user.library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import {getHashedIDFromMyStuffURL} from '@Libraries/my-stuff/my-stuff-library';
import {PMW_FEATURE_CONSTANTS} from '@Panels/premium-pricing-panel/premium-pricing-panel.constants';
import {isSubset} from '@Utils/array.util';
import {NUM_SECONDS_PER_HOUR, NUM_SECONDS_PER_MINUTE, getDateFromUnixTimestamp, getTimeToDate} from '@Utils/date.util';
import {downloadBlobAsFile} from '@Utils/file.util';
import {formatNumber, formatNumberInUSD, formatWholeNumberInUSD} from '@Utils/number.util';
import type {CheckInBannerEvent} from '@Components/my-stuff-check-in-banner/my-stuff-check-in-banner.types';
import {serializeUrl} from '@Utils/url.util';


/** The ticketing marketing page (m/event-ticketing-platform) — where the wizard banner's Learn more goes. */
export const TICKETING_MARKETING_PAGE_PATH = 'm/event-ticketing-platform';

/** Which money/check-in states an event's orders can reach — see getVisibleTicketingStatusFilterOptions. */
export interface TicketingStatusFilterScope {
  isRsvp: boolean;
  isFree: boolean;
}

/**
 * Which order list a filter selection maps to: the cached full list when default, else the filtered list.
 * Callers pass live filters or a request's own filters, so an in-flight page lands in the right list.
 */
export const getActiveOrdersList = (state: TicketingDashboardState, search: string, statuses: TicketingStatusFilterValue[]): TicketingOrdersListLazyData => {
  return areTicketingOrderFiltersDefault(search, statuses) ? state.defaultOrders : state.filteredOrders;
};

/**
 * An RSVP (registration-only) event rather than paid ticketing. Requires `isSelected`: a deselected config is
 * retained on save (see TicketingConfigVO::setIsSelected), so switching away from RSVP leaves `isRsvp: true`
 * behind, and treating that as RSVP surfaces a dashboard for an event that no longer has one.
 */
export const isRsvpEvent = (event: EventVO): boolean => {
  const config = event.ticketingConfig;
  return config?.isSelected === true && config.isRsvp;
};

/**
 * A free ($0) ticketing event: paid-ticketing mode with a zero price. Issues tickets but collects no money, so the
 * dashboard shows the count-based stats row (tickets sold / page views / cancellations) instead of the money cards.
 */
export const isFreeTicketingEvent = (event: EventVO): boolean => {
  const config = event.ticketingConfig;
  if (config === undefined || config.isRsvp) {
    return false;
  }
  return (config.price ?? 0) <= 0;
};

/**
 * A ticketed event: ticketing selected AND not RSVP — issues tickets (paid or free), not RSVP registrations.
 * Used to label the My Stuff performance column "Tickets" vs "Registrations".
 */
export const isTicketedEvent = (event: EventVO): boolean => {
  const config = event.ticketingConfig;
  return config?.isSelected === true && !config.isRsvp;
};

/**
 * A paid ticketed event: issues tickets AND collects money (ticketed, not RSVP, not free). Its My Stuff actions
 * row leads with the Tickets CTA; other events lead with Promote instead.
 */
export const isPaidTicketedEvent = (event: EventVO): boolean => {
  return isTicketedEvent(event) && !isFreeTicketingEvent(event);
};

/**
 * Whether the event has a ticketing dashboard (paid, free, or RSVP). Checks ticketing is actually enabled
 * (selected, or RSVP), not merely that a leftover unselected config object exists.
 */
export const hasTicketingDashboard = (event: EventVO): boolean => {
  return isTicketedEvent(event) || isRsvpEvent(event);
};

/**
 * Whether the legacy signup form is what collects this event's registrations — the question the manage page's
 * "Registrations" and signup-form cards exist to answer. Every other registration mode says no:
 *
 * - **Tickets / RSVP.** Registrations live in Hi.Events and are shown on the ticketing dashboard, so these cards
 *   would sit beside the real numbers listing stale signup-form contacts.
 * - **Custom link.** The register button sends the visitor off-site, so PMW collects nothing.
 * - **None.** `areRegistrationsRequired` false means the event page renders no register button at all
 *   (EventRegister.php), so there is nothing to collect.
 *
 * `registerBtnLink` is tested for truthiness, not against `undefined`: an event with no link stores PHP `null`,
 * and the wizard clears it to `undefined`.
 */
export const doesEventUseLegacySignupForm = (event: EventVO): boolean => {
  if (hasTicketingDashboard(event)) {
    return false;
  }
  const {areRegistrationsRequired, registerBtnLink} = event.registrationFields;
  return areRegistrationsRequired && !registerBtnLink;
};

/**
 * Mirrors the backend `EventTicketingFacade::isPaidTicketingFeatureAvailable` — whether this user may use PAID
 * ticketing. RSVP is never gated by this. Sourced from the MyStuff page prop.
 */
export const isPaidTicketingFeatureAvailable = (): boolean => {
  return !!window.reactPageProps?.isPaidTicketingFeatureAvailable;
};

/**
 * Whether the viewer's country supports event ticketing. Sourced from the wizard and MyStuff page props; absent
 * elsewhere ⇒ false (treated as unsupported).
 */
export const isEventTicketingCountrySupported = (): boolean => {
  return !!window.reactPageProps?.isTicketingCountrySupported;
};

/**
 * Whether this organiser could sell paid tickets today: the feature is on for them, their country supports it,
 * and their plan allows provisioning. All four are needed — `EventWizardTicketing.selectMode` turns the same
 * four into two different rejections (a "coming soon" dialog for the first two, an upsell for the last two), so
 * anything that merely *offers* paid ticketing has to agree with it about who can take the offer.
 */
export const isOrganiserEligibleForPaidTicketing = (): boolean => {
  return isPaidTicketingFeatureAvailable() && isEventTicketingCountrySupported() && isUserPremium() && !isUserTrial();
};

/**
 * Mirrors the backend `EventTicketingFacade::isTicketingAvailableForEvent` — the per-event gate for the Tickets
 * dashboard, tab, CTAs, and navigation. RSVP is always available; paid ticketing needs the feature to be on for
 * this viewer. A null event (still resolving) is treated as paid, so it stays gated until it loads.
 *
 * This answers "may this viewer use ticketing here", not "does this event have ticketing" — pair it with
 * `hasTicketingDashboard` where the surface also needs the event to actually be ticketed.
 */
export const isTicketingAvailableForEvent = (event: EventVO | null): boolean => {
  if (isPaidTicketingFeatureAvailable()) {
    return true;
  }
  return !!event && isRsvpEvent(event);
};

/**
 * Whether a surface may offer this event's ticketing dashboard. Both halves are load-bearing and neither implies the
 * other: `isTicketingAvailableForEvent` answers "may this viewer use ticketing here", which is true of *every* event
 * once the paid feature is on for them — so on its own it offers a dashboard for an event that was never ticketed and
 * has no Hi.Events provisioning behind it.
 *
 * Shared by the three doors into that page — the events-list row CTA, that row's options menu, and the event sub-nav
 * tab — so they cannot disagree about whether the door exists. A null event has not resolved yet and is refused, so no
 * surface offers the door before it knows what the event is.
 */
export const canOpenTicketingDashboardForEvent = (event: EventVO | null): boolean => {
  return event !== null && hasTicketingDashboard(event) && isTicketingAvailableForEvent(event);
};

/**
 * The ticketing error code off an ajax rejection. readLocal/writeLocal throw an Error that core.js
 * `getErrorAjaxResponse` decorates with the response's top-level `code`; anything else (a network drop, a
 * non-ticketing failure) has none, and reads as null.
 */
export const getTicketingErrorCodeFromAjaxError = (error: unknown): string | null => {
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
    return error.code;
  }
  return null;
};

/**
 * The ticketing error MESSAGE off an ajax rejection — already translated and already safe to show. Every
 * TicketingException builds its message from a lang key (even the catch-all: TicketingUnknownException uses
 * `lang($errorCode->getLangKey())`), and TicketingErrorResponder mirrors it to the envelope's top-level
 * `message` precisely so a React caller can tell one failure from another. So a surface that has this can say
 * "Refunds are closed within 24 hours of the event start" instead of a generic retry line.
 *
 * Reads both rejection shapes the ticketing surfaces produce: the Error core.js `getErrorAjaxResponse` throws
 * from readLocal/writeLocal, and the SerializedError a createAsyncThunk `.unwrap()` rethrows (RTK preserves
 * `message` and `code`). A network drop or a non-ticketing failure carries no usable message and reads as null,
 * which callers turn back into their own generic copy.
 */
export const getTicketingErrorMessageFromAjaxError = (error: unknown): string | null => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message !== '') {
    return error.message;
  }
  return null;
};

/**
 * Whether the event is over. Wraps `isPastEvent` to add the no-end-date case: that helper turns `endDateTimestamp`
 * straight into a Date, so a zero timestamp becomes 1970 and reads as long past. An event with no end has no end to
 * be past, and keeps its check-in — the same reading `isEventCheckInWindowOpen` takes.
 */
const hasEventEnded = (event: EventVO): boolean => {
  return event.endDateTimestamp > 0 && isPastEvent(event);
};

/**
 * Whether door check-in is offered for this event. It is withheld for two reasons:
 *   - **RSVP.** A registration is not a ticket, so there is nothing to present at the door — RSVP events get no
 *     check-in surface at all: no scanner, no check-in view, no per-order or per-attendee action.
 *   - **The event is over.** Nobody arrives at a finished event, so a tab left open past the end must not still
 *     admit people. An event yet to start still offers check-in (doors open early).
 *
 * This is the ONE switch. Every check-in entry point resolves to this predicate (the surfaces that own an event
 * call it and pass the result down as a plain flag), so changing what check-in requires means changing this body
 * and the matching backend guard, `EventTicketingFacade::isCheckInAvailableForEvent`.
 */
export const isCheckInAvailableForEvent = (event: EventVO): boolean => {
  return !isRsvpEvent(event) && !hasEventEnded(event);
};

/**
 * What to call an order's public id on screen. An RSVP attendee never placed an order — they registered — so the
 * orders table header and both attendee-modal bodies label the same id "Registration ID" there and "Order ID"
 * everywhere else.
 */
export const getTicketingOrderIdLabel = (isRsvp: boolean): string => {
  return isRsvp ? window.i18next.t('pmwjs_ticketing_registration_id') : window.i18next.t('pmwjs_ticketing_order_details_order_id');
};

/**
 * What to call the ticketing dashboard wherever a surface links to it — "RSVPs" on an RSVP event, "Tickets"
 * otherwise. Shared by the events-list row CTA, that row's options menu, and the event sub-nav tab, so the three
 * ways into the same page cannot disagree about what it holds.
 *
 * The server-rendered calendar/schedule row makes the same call in `EventListItemCtaHelper::getTicketingCtaText()`
 * off `event_lang.event_rsvps` / `event_tickets`. Reword one and reword the other, or the two rows disagree.
 */
export const getTicketingSectionLabel = (event: EventVO | null): string => {
  return event !== null && isRsvpEvent(event) ? window.i18next.t('pmwjs_rsvps') : window.i18next.t('pmwjs_tickets');
};

/**
 * The "of {total} tickets" / "of {total} registrations" tail of an "N of M" count. The count names its own unit,
 * because "1 of 2" alone leaves the reader to guess what is being counted. Shared by the attendee modal's per-ticket
 * switcher and the refund/cancel flow's quantity stepper — both of which only render above a count of one, so the
 * unit is always plural.
 */
export const getTicketingOfTotalLabel = (isRsvp: boolean, total: number): string => {
  return isRsvp ? window.i18next.t('pmwjs_of_total_registrations', {total}) : window.i18next.t('pmwjs_of_total_tickets', {total});
};

/**
 * The cancel action's label, pluralised by count and registration-worded on RSVP events. Shared by the attendee
 * modal's destructive footer button and the cancel flow's confirm CTA, so the button and the step it opens can't
 * drift apart.
 */
export const getTicketingCancelActionLabel = (isRsvp: boolean, ticketCount: number): string => {
  if (isRsvp) {
    return ticketCount === 1 ? window.i18next.t('pmwjs_ticketing_cancel_registration') : window.i18next.t('pmwjs_ticketing_cancel_registrations');
  }
  return ticketCount === 1 ? window.i18next.t('pmwjs_ticketing_cancel_ticket') : window.i18next.t('pmwjs_ticketing_cancel_tickets');
};

/**
 * Whether the My Stuff router may open the Tickets page for the event named in the URL. RSVP dashboards are
 * never gated by the paid flag, so this can only refuse when the events store already knows the event is a paid
 * one the viewer can't open — that covers in-session navigation. A cold deep-link has no event in the store yet
 * and is allowed through to the dashboard, whose own gate (and the backend's) turns it away after the fetch.
 */
export const canOpenEventTicketsPage = (): boolean => {
  const event = window.PMW.redux.store.getState().events.allEvents[getHashedIDFromMyStuffURL()];
  return !event || isTicketingAvailableForEvent(event);
};

const getEventTicketingDashboardHashForEventHid = (eventHashedID: EventHashedID): string => {
  return `${MY_STUFF_HASHES.eventTickets}/${eventHashedID}`;
};

const getEventTicketingDashboardHash = (event: EventVO): string => {
  return getEventTicketingDashboardHashForEventHid(event.hashedID);
};

/**
 * Opens the event's ticketing dashboard with one door-check-in surface already open. Every entry point sits inside My
 * Stuff, so moving the hash is enough to get there and the page survives the trip; writing the flag into the query
 * string through window.location would instead be a document navigation and reload the whole of My Stuff.
 *
 * The flag is stamped on after the hash moves so it belongs to the dashboard's own history entry — the page the
 * organiser came from keeps a clean URL to go back to. The dashboard reads the flag once its fetch resolves, well
 * after this returns.
 *
 * Takes an event hid rather than an `EventVO` because the caller is the My Stuff check-in banner, which is fed by an
 * endpoint rather than the events store and so has no VO to hand. Nothing is gated here: the banner's own endpoint
 * already applied the ticketing-availability check, and the dashboard re-gates on arrival anyway — which is exactly
 * what already happens to a cold deep-link, where no event is in the store to check against either.
 */
const goToEventTicketingDashboardWithSurfaceOpen = (eventHashedID: EventHashedID, surfaceQueryParam: string): void => {
  window.location.hash = getEventTicketingDashboardHashForEventHid(eventHashedID);
  const urlWithSurfaceFlag = new URL(window.location.href);
  urlWithSurfaceFlag.searchParams.set(surfaceQueryParam, '1');
  window.history.replaceState(window.history.state, '', serializeUrl(urlWithSurfaceFlag));
};

/**
 * Straight into QR scanning — the phone's door-check-in surface, where the camera IS the tool. One tap from My Stuff
 * to scanning at the door.
 */
export const goToEventTicketingScannerForEvent = (eventHashedID: EventHashedID): void => {
  goToEventTicketingDashboardWithSurfaceOpen(eventHashedID, OPEN_SCANNER_QUERY_PARAM);
};

/**
 * Straight into the check-in VIEW — the desktop door surface: the searchable attendee list an organiser works from
 * with a keyboard, rather than a camera they would have to hold up to a laptop.
 */
export const goToEventTicketingCheckInViewForEvent = (eventHashedID: EventHashedID): void => {
  goToEventTicketingDashboardWithSurfaceOpen(eventHashedID, OPEN_CHECK_IN_VIEW_QUERY_PARAM);
};

/**
 * The dashboard's URL rather than a jump to it, for callers that need the address itself — the wizard hands it to
 * its post-save redirect, which decides between this and Promote before navigating anywhere.
 */
export const getTicketingDashboardURL = (event: EventVO): string => {
  return window.PMW.util.site_url(`posters/mine${getEventTicketingDashboardHash(event)}`);
};

export const goToEventTicketingDashboard = (event: EventVO): void => {
  if (isTicketingAvailableForEvent(event)) {
    window.location.href = getTicketingDashboardURL(event);
  }
};

/**
 * Reads and clears one of the "open this surface on arrival" flags the My Stuff check-in banner adds when it
 * deep-links into the dashboard. Cleared as it is read so a refresh doesn't reopen the surface behind the organiser.
 */
const consumeSurfaceFlagFromUrl = (surfaceQueryParam: string): boolean => {
  const url = new URL(window.location.href);
  if (url.searchParams.get(surfaceQueryParam) === null) {
    return false;
  }
  url.searchParams.delete(surfaceQueryParam);
  window.history.replaceState(null, '', serializeUrl(url));
  return true;
};

export const consumeOpenScannerFlagFromUrl = (): boolean => {
  return consumeSurfaceFlagFromUrl(OPEN_SCANNER_QUERY_PARAM);
};

export const consumeOpenCheckInViewFlagFromUrl = (): boolean => {
  return consumeSurfaceFlagFromUrl(OPEN_CHECK_IN_VIEW_QUERY_PARAM);
};

/**
 * The event has ticketing activity once any ticket has sold, money has come in, or a refund exists.
 * Before that the dashboard shows the empty state instead of the stats row + orders table.
 */
export const hasTicketingActivity = (stats: TicketingStats): boolean => {
  return stats.ticketsSold > 0 || stats.totalSalesGross > 0 || stats.refundsCount > 0;
};

/**
 * The one event the My Stuff check-in banner should offer, or null when none qualifies. One request per My Stuff
 * page load; the backend picks the event, so the browser never needs the events store — which is the point, because
 * only the events tab loads it.
 *
 * Every gate lives in `EventTicketingDashboardFacade::getCheckInBannerEvent` (own event, ticketing dashboard the
 * viewer may open, check-in applies, inside the pre-start lead through event end). Do not re-test any of them here:
 * a second copy of the window would only ever disagree with the first.
 */
export const fetchCheckInBannerEvent = async (): Promise<CheckInBannerEvent | null> => {
  const response = (await window.PMW.readLocal('eventticketing/getTicketingCheckInBanner')) as {event: CheckInBannerEvent | null};
  return response.event;
};

/**
 * How long until the event starts, as an already-translated phrase — "in a minute", "in 40 minutes", "in 2 hours".
 *
 * Leans on `getTimeToDate`, which owns that whole ladder in every locale; minting plural keys here would duplicate
 * it badly. Its one unusable answer is the sub-minute case, where it says "just now" — a countdown must not read
 * "starting just now" while the event has not started — so anything under a minute is floored to "in a minute".
 */
const getTimeToEventStartLabel = (startDateTimestamp: number, nowSeconds: number): string => {
  if (startDateTimestamp - nowSeconds < NUM_SECONDS_PER_MINUTE) {
    return window.i18next.t('pmwjs_in_a_minute');
  }
  return getTimeToDate(getDateFromUnixTimestamp(startDateTimestamp));
};

/**
 * The My Stuff banner's headline, which names the event because the reader is anywhere in My Stuff and has no other
 * clue which event this is about: "{title} is starting in 40 minutes", then "{title} has started".
 */
export const getCheckInBannerTitle = (bannerEvent: CheckInBannerEvent, nowSeconds: number): string => {
  if (bannerEvent.startDateTimestamp - nowSeconds <= 0) {
    return window.i18next.t('pmwjs_ticketing_event_has_started', {eventTitle: bannerEvent.title});
  }
  return window.i18next.t('pmwjs_ticketing_event_is_starting', {
    eventTitle: bannerEvent.title,
    timeToStart: getTimeToEventStartLabel(bannerEvent.startDateTimestamp, nowSeconds),
  });
};

/**
 * The ticketing dashboard's check-in bar headline. Deliberately does NOT name the event: the organiser is already on
 * that event's page, so repeating its title in the bar is noise. Same two phases as the My Stuff banner, so the two
 * surfaces never disagree about whether an event has started.
 */
export const getEventTicketingCheckInBarTitle = (event: EventVO, nowSeconds: number): string => {
  if (event.startDateTimestamp - nowSeconds <= 0) {
    return window.i18next.t('pmwjs_ticketing_check_in_bar_started_title');
  }
  return window.i18next.t('pmwjs_ticketing_check_in_bar_starting_title', {
    timeToStart: getTimeToEventStartLabel(event.startDateTimestamp, nowSeconds),
  });
};

/**
 * Whether the banner's event has finished, so a My Stuff tab left open past the end stops offering a door that has
 * closed. The backend already refused an ended event at fetch time; this is what catches the end passing *during*
 * the session. A missing or zero end never reads as ended, matching `hasEventEnded`.
 */
export const hasCheckInBannerEventEnded = (bannerEvent: CheckInBannerEvent, nowSeconds: number): boolean => {
  return bannerEvent.endDateTimestamp > 0 && nowSeconds > bannerEvent.endDateTimestamp;
};

/**
 * How long before an event starts its check-in surfaces appear. Door staff want the door open a little early, so the
 * bar and the My Stuff banner both lead the start by this much.
 *
 * ⚠️ **This number exists twice on purpose, and the copies must agree.** The backend needs it to filter the My Stuff
 * banner's candidate query in SQL (`EventTicketingDashboardFacade::CHECK_IN_BANNER_LEAD_SECONDS`); the frontend needs
 * it here because the ticketing dashboard decides its own bar from an `EventVO` it already holds, with no request to
 * ask. Change one and change the other.
 */
const CHECK_IN_BAR_LEAD_SECONDS = 2 * NUM_SECONDS_PER_HOUR;

/**
 * Whether an event's check-in surfaces should show by time: from `CHECK_IN_BAR_LEAD_SECONDS` before the start through
 * the end. `EventVO` timestamps are Unix seconds; a missing or zero end keeps the window open once started, since an
 * event with no end has no end to be past.
 *
 * Takes `nowSeconds` rather than reading the clock itself so the caller's tick drives it — a component that owns a
 * ticking timestamp gets a window that opens and closes on its own, and a `new Date()` read in here would be
 * non-reactive under React Compiler and could be replayed from cache.
 */
export const isEventCheckInWindowOpen = (event: EventVO, nowSeconds: number): boolean => {
  const startSeconds = event.startDateTimestamp;
  if (startSeconds <= 0 || nowSeconds < startSeconds - CHECK_IN_BAR_LEAD_SECONDS) {
    return false;
  }
  const endSeconds = event.endDateTimestamp;
  return endSeconds <= 0 || nowSeconds <= endSeconds;
};

// Matches the door-scan URL `eventticketing/checkIn/{eventHid}/{attendeePublicId}` inside the decoded text
// regardless of scheme/host/prefix. Segments stop at /, ?, or # so query strings don't bleed into the attendee id.
const CHECK_IN_QR_PATH_PATTERN = /eventticketing\/checkIn\/([^/?#]+)\/([^/?#]+)/;

/**
 * Parse a scanned QR's decoded text into its event + attendee, or null when it is not a ticket check-in URL.
 * The scanner still verifies the parsed eventHashedID matches the open event — a QR for another event parses
 * fine here but is rejected as the wrong event.
 */
export const parseCheckInQrText = (decodedText: string): DecodedCheckInQr | null => {
  const match = CHECK_IN_QR_PATH_PATTERN.exec(decodedText);
  if (match === null) {
    return null;
  }
  return {eventHashedID: match[1], attendeePublicId: match[2]};
};

/** A placeholder order for the table's cold-load skeleton rows; its fields are unused (the row renders skeletons). */
export const getDummyTicketingOrderSummary = (): TicketingOrderSummary => {
  return {
    orderId: 0,
    orderPublicId: '',
    buyerName: '',
    buyerEmail: '',
    ticketCount: 0,
    activeTicketCount: 0,
    cancelledTicketCount: 0,
    refundedTicketCount: 0,
    checkedInCount: 0,
    orderStatus: OrderAttendanceStatus.REGISTERED,
    paymentStatus: null,
    refundStatus: TicketingRefundStatus.NONE,
    createdAtTimestamp: 0,
  };
};

/**
 * Whether an order still has live tickets (not refunded, not canceled). A fully canceled or refunded order is
 * terminal and offers no actions but "View".
 */
const isActiveOrder = (order: TicketingOrderSummary): boolean => {
  return order.orderStatus !== OrderAttendanceStatus.CANCELLED && order.refundStatus !== TicketingRefundStatus.REFUNDED;
};

/**
 * Order-level availability predicates for the order-scoped actions. The orders-table row and the tablet kebab menu
 * pick from these (mark-as-paid > check-in > view); neither exposes a refund action.
 */
// Whether an order is in a pending-refund state needing the organiser to act in the order-details modal — QUEUED
// (buyer refund queued because the connected balance was short, awaiting top-up + retry) or REQUESTED (buyer asked
// <24h out, awaiting approval). The row uses this to show "View" rather than "Check in".
export const isOrderAwaitingRefundAction = (order: TicketingOrderSummary): boolean => {
  return order.refundStatus === TicketingRefundStatus.QUEUED || order.refundStatus === TicketingRefundStatus.REQUESTED;
};

export const isMarkAsPaidOrderActionAvailable = (order: TicketingOrderSummary): boolean => {
  return isActiveOrder(order) && order.paymentStatus === PaymentStatus.UNPAID;
};

// Counted against the ACTIVE tickets, matching the backend's own orderStatus derivation. Against the total ever
// issued, an order of 3 with 1 cancelled and 2 checked in would offer Check in on a row whose pill already reads
// "Checked in".
export const isCheckInOrderActionAvailable = (order: TicketingOrderSummary): boolean => {
  return isActiveOrder(order) && order.checkedInCount < order.activeTicketCount;
};

// Joins the cancelled and the refunded phrase when an order has both, e.g. "2 tickets cancelled, 1 ticket refunded".
const CANCELLED_AND_REFUNDED_TEXT_SEPARATOR = ', ';

const getPluralisedTicketCountText = (count: number, singularKey: string, pluralKey: string): string => {
  return count === 1 ? window.i18next.t(singularKey) : window.i18next.t(pluralKey, {count});
};

/**
 * What an order lost, for the tooltip beside its ticket count: the non-zero cancelled and refunded counts,
 * pluralised. The count on the row and the card is every ticket ever issued, so without this an order whose
 * tickets were partly cancelled or refunded reads exactly like one that still has all of them.
 *
 * null when the order kept every ticket it was issued — that is what tells the row and the card to show no
 * info affordance at all.
 */
export const getCancelledAndRefundedTicketsText = (order: TicketingOrderSummary): string | null => {
  const phrases: string[] = [];
  if (order.cancelledTicketCount > 0) {
    phrases.push(getPluralisedTicketCountText(order.cancelledTicketCount, 'pmwjs_ticketing_one_ticket_cancelled', 'pmwjs_ticketing_n_tickets_cancelled'));
  }
  if (order.refundedTicketCount > 0) {
    phrases.push(getPluralisedTicketCountText(order.refundedTicketCount, 'pmwjs_ticketing_one_ticket_refunded', 'pmwjs_ticketing_n_tickets_refunded'));
  }
  return phrases.length > 0 ? phrases.join(CANCELLED_AND_REFUNDED_TEXT_SEPARATOR) : null;
};

// Shown on both the orders table row and the order-details ticket card, so it lives here (unlike the per-row and
// per-attendee status pills, scoped to their one consumer).
export const getPaymentStatusPill = (paymentStatus: PaymentStatus): TicketingStatusPillProps => {
  switch (paymentStatus) {
    case PaymentStatus.CARD:
      return {variant: TicketingStatusPillVariant.NEUTRAL_OUTLINED, label: window.i18next.t('pmwjs_ticketing_stat_sales_card'), icon: 'icon-cc-generic'};
    case PaymentStatus.CASH:
      return {variant: TicketingStatusPillVariant.NEUTRAL_OUTLINED, label: window.i18next.t('pmwjs_ticketing_stat_sales_cash'), icon: 'icon-money'};
    case PaymentStatus.UNPAID:
      return {variant: TicketingStatusPillVariant.SOFT_DANGER, label: window.i18next.t('pmwjs_ticketing_stat_sales_unpaid'), icon: 'icon-clock-o'};
    case PaymentStatus.REFUNDED:
      // Gray (neutral), like the Cancelled pill — a refunded ticket is a closed/void state, not an error to flag.
      return {variant: TicketingStatusPillVariant.NEUTRAL, label: window.i18next.t('pmwjs_ticketing_refund_status_refunded'), icon: 'icon-rotate-ccw'};
  }
};

/**
 * The question answers to show for one ticket (mirrors the backend TicketingOrder::getQuestionAnswersForTicket).
 *
 * Decided per ANSWER, from the tag it was stored with — an untagged answer was asked once for the whole order so it
 * applies to every ticket, and a tagged one was asked of that person so it applies only to them. Deliberately NOT from
 * the event's current collection mode: that can be changed after answers exist, and then it describes neither what was
 * asked nor how. Reading the mode instead used to drop every order-level answer on a per-attendee event (null matches
 * no attendee id), and conversely show one attendee's answer on all of them on a per-order event.
 */
export const getQuestionAnswersForTicket = (questionAnswers: TicketingQuestionAnswer[], attendeePublicId: string): TicketingQuestionAnswer[] => {
  return questionAnswers.filter((questionAnswer) => questionAnswer.attendeePublicId === null || questionAnswer.attendeePublicId === attendeePublicId);
};

// One row of the orders status-filter dropdown: the value the backend statuses[] param reads, plus its label.
export interface TicketingStatusFilterOption {
  value: TicketingStatusFilterValue;
  labelKey: string;
}

// PARTIALLY_CHECKED_IN is folded into "Checked in" backend-side, so no separate row. Only fully "Refunded" is a
// filter — a partial refund is deliberately not a surfaced state on its own (it shows once the order is cancelled).
// REQUESTED (a buyer's <24h refund request awaiting the organiser's action) gets its own row so the organiser can
// find those; QUEUED stays unfilterable for now. REQUESTED has no settled Hi.Events refund_status, so the backend
// serves it via the capped PMW-side scan (TicketingOrdersFilterOpts::needsPmwSideFiltering).
export const TICKETING_STATUS_FILTER_OPTIONS: TicketingStatusFilterOption[] = [
  {value: OrderAttendanceStatus.REGISTERED, labelKey: 'pmwjs_ticketing_order_status_awaiting_arrival'},
  {value: OrderAttendanceStatus.CHECKED_IN, labelKey: 'pmwjs_ticketing_order_status_checked_in'},
  {value: OrderAttendanceStatus.CANCELLED, labelKey: 'pmwjs_ticketing_order_status_cancelled'},
  {value: TicketingRefundStatus.REFUNDED, labelKey: 'pmwjs_ticketing_refund_status_refunded'},
  {value: TicketingRefundStatus.REQUESTED, labelKey: 'pmwjs_ticketing_refund_requested_status'},
];

// Always-explicit filter (R1-02): the default view has every value selected ("All"), and both ends treat the full
// set as unfiltered — routes to the cached default list, and the backend takes its fast unfiltered paging path.
export const ALL_TICKETING_STATUS_FILTER_VALUES: TicketingStatusFilterValue[] = TICKETING_STATUS_FILTER_OPTIONS.map((option) => option.value);

export const isEveryStatusFilterSelected = (activeStatusFilters: TicketingStatusFilterValue[]): boolean => {
  return isSubset(ALL_TICKETING_STATUS_FILTER_VALUES, activeStatusFilters);
};

// The two money rows. Neither can occur without a charge to reverse, so both drop out on any event that takes no
// money — RSVP and free ($0) ticketed alike.
const REFUND_STATUS_FILTER_VALUES: TicketingStatusFilterValue[] = [TicketingRefundStatus.REFUNDED, TicketingRefundStatus.REQUESTED];

/**
 * The status rows the dropdown offers, narrowed to the states this event's orders can actually reach.
 *
 * - **RSVP** keeps only "still coming" and "cancelled": there is no door check-in, and no money to refund. Its
 *   registered row reads "Registered" (the row pill's wording) rather than "Awaiting arrival", which means "not
 *   checked in" and so says nothing on an event with no check-in.
 * - **Free ($0) ticketed** keeps check-in — free events still issue tickets and scan them — but drops the two
 *   refund rows, since nothing was ever charged.
 * - **Paid ticketed** keeps all five.
 *
 * Dropped values stay in the stored selection while every visible row is checked — see the status filter's
 * `onStatusFiltersChange` for why that matters.
 */
export const getVisibleTicketingStatusFilterOptions = ({isRsvp, isFree}: TicketingStatusFilterScope): TicketingStatusFilterOption[] => {
  if (isRsvp) {
    return [
      {value: OrderAttendanceStatus.REGISTERED, labelKey: 'pmwjs_ticketing_attendance_registered'},
      {value: OrderAttendanceStatus.CANCELLED, labelKey: 'pmwjs_ticketing_order_status_cancelled'},
    ];
  }
  if (isFree) {
    return TICKETING_STATUS_FILTER_OPTIONS.filter((option) => !REFUND_STATUS_FILTER_VALUES.includes(option.value));
  }
  return TICKETING_STATUS_FILTER_OPTIONS;
};

/** No search and every status selected ("All") — the table shows the cached full list, not a filtered fetch. */
export const areTicketingOrderFiltersDefault = (search: string, activeStatusFilters: TicketingStatusFilterValue[]): boolean => {
  return search === '' && isEveryStatusFilterSelected(activeStatusFilters);
};

// R1-10: the table groups orders by lifecycle state, most actionable first. Refunded sits between checked-in and
// cancelled; it shares CANCELLED order status, so the rank splits that case on the refund status.
enum TicketingOrderSortRank {
  REGISTERED,
  PARTIALLY_CHECKED_IN,
  CHECKED_IN,
  REFUNDED,
  CANCELLED,
}

const getTicketingOrderSortRank = (order: TicketingOrderSummary): TicketingOrderSortRank => {
  switch (order.orderStatus) {
    case OrderAttendanceStatus.REGISTERED:
      return TicketingOrderSortRank.REGISTERED;
    case OrderAttendanceStatus.PARTIALLY_CHECKED_IN:
      return TicketingOrderSortRank.PARTIALLY_CHECKED_IN;
    case OrderAttendanceStatus.CHECKED_IN:
      return TicketingOrderSortRank.CHECKED_IN;
    case OrderAttendanceStatus.CANCELLED:
      return order.refundStatus === TicketingRefundStatus.REFUNDED ? TicketingOrderSortRank.REFUNDED : TicketingOrderSortRank.CANCELLED;
  }
};

/**
 * The display order of the orders table/list (R1-10): sorted by status group over the LOADED set only, newest
 * first within a group. Backend paging is untouched, so a later page's order slots into its group once loaded.
 */
export const sortTicketingOrdersByStatus = (orders: TicketingOrderSummary[]): TicketingOrderSummary[] => {
  return [...orders].sort((a, b) => {
    const rankDifference = getTicketingOrderSortRank(a) - getTicketingOrderSortRank(b);
    return rankDifference !== 0 ? rankDifference : b.createdAtTimestamp - a.createdAtTimestamp;
  });
};

/** Narrows loaded orders by the live-typed search term — attendee name, email, or order id, case-insensitive. */
export const filterTicketingOrdersBySearchTerm = (orders: TicketingOrderSummary[], term: string): TicketingOrderSummary[] => {
  const lowerTerm = term.trim().toLowerCase();
  return orders.filter(
    (order) => order.buyerName.toLowerCase().includes(lowerTerm) || order.buyerEmail.toLowerCase().includes(lowerTerm) || order.orderPublicId.toLowerCase().includes(lowerTerm)
  );
};

// The zero-rows rule shared by the orders table, mobile list, and search overlay's add-attendee row — they must
// agree on when the empty state is on screen (the overlay hides its row in favour of it).
export const isTicketingOrdersEmptyStateShowing = (orders: TicketingOrderSummary[], areOrdersLoading: boolean): boolean => {
  return orders.length === 0 && !areOrdersLoading;
};

const TICKETING_ATTENDEES_DOWNLOAD_ENDPOINT = 'eventticketing/downloadTicketingAttendees';

// The download endpoint (B10) streams the file with a Content-Disposition header, so read the filename the backend
// set; falls back to a generic name only when the header is missing.
const getAttendeesExportFilename = (response: Response, format: TicketingAttendeeExportFormat): string => {
  const contentDisposition = response.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename="?([^";]+)"?/.exec(contentDisposition);
  return filenameMatch?.[1] ?? `attendees.${format}`;
};

// A failed download comes back as the ticketing JSON error envelope (not a file), so it carries a JSON content type.
const isJsonErrorResponse = (response: Response): boolean => {
  return (response.headers.get('Content-Type') ?? '').toLowerCase().includes('application/json');
};

const readDownloadError = async (response: Response): Promise<Error> => {
  const genericError = new Error(window.i18next.t('pmwjs_error_occurred_try_again'));
  try {
    const body = (await response.json()) as {message?: string};
    return body.message ? new Error(body.message) : genericError;
  } catch {
    return genericError;
  }
};

/**
 * Downloads the event's attendee list as a CSV or PDF file (B10). Fetches same-origin (login cookie rides along)
 * and saves the blob; readLocal can't be used because it unwraps a JSON envelope. Throws on failure.
 */
export const downloadTicketingAttendees = async (eventHashedID: EventHashedID, format: TicketingAttendeeExportFormat): Promise<void> => {
  const params = new URLSearchParams({event_hid: eventHashedID, format});
  const url = `${window.PMW.util.site_url(TICKETING_ATTENDEES_DOWNLOAD_ENDPOINT)}?${params.toString()}`;
  const response = await fetch(url, {credentials: 'same-origin'});
  if (!response.ok || isJsonErrorResponse(response)) {
    throw await readDownloadError(response);
  }
  downloadBlobAsFile(await response.blob(), getAttendeesExportFilename(response, format));
};

/**
 * Does this plan put a number on ticket sales at all? The wizard line states that number whenever there is one,
 * with no regard for how much of it the organiser has used — at setup time they are being told what their plan
 * covers, not warned. Premium Plus declares neither limit, so it says nothing.
 */
export const doesPlanDeclareLimit = (planLimit: TicketingPlanLimit): boolean => {
  return planLimit.thresholdTickets !== null || planLimit.thresholdGross !== null;
};

/** What the plan allows, for the wizard's info line: "Your plan caps total sales at $2,500 or 100 tickets." */
export const getPlanLimitAllowanceMessage = (planLimit: TicketingPlanLimit): string => {
  return window.i18next.t('pmwjs_your_plan_caps_total_sales_at_x', {allowance: getPlanAllowancePhrase(planLimit)});
};

/**
 * Whether the dashboard has anything to say about where the organiser stands. Unlike the wizard line this is
 * state-driven: it appears only once they are near the limit, because on the dashboard it is a warning.
 */
export const shouldWarnAboutPlanLimit = (planLimit: TicketingPlanLimit): boolean => {
  return planLimit.state !== TicketingPlanLimitState.NONE;
};

/**
 * The rate the organiser is now being charged because they passed their allowance, or null when that has not
 * happened — either they are still under it, or their plan's allowance carries no fee consequence at all
 * (a tier can declare one purely as an entitlement, and telling that organiser their fee rose would be false).
 *
 * Returning the rate rather than a boolean is what lets the banner's tone and its sentence read the same
 * condition without stating it twice.
 */
const getRaisedFeeRate = (planLimit: TicketingPlanLimit): number | null => {
  return planLimit.state === TicketingPlanLimitState.CROSSED ? planLimit.overagePercent : null;
};

/** True once passing the allowance has actually raised the fee — the only state that warrants the red treatment. */
export const hasPlanLimitRaisedFee = (planLimit: TicketingPlanLimit): boolean => {
  return getRaisedFeeRate(planLimit) !== null;
};

/**
 * Where the organiser stands, for the dashboard banner. Two sentences, matching the two treatments: red once
 * the fee has actually gone up, amber for everything before that.
 */
export const getPlanLimitStandingMessage = (planLimit: TicketingPlanLimit): string => {
  const raisedFeeRate = getRaisedFeeRate(planLimit);

  if (raisedFeeRate !== null) {
    return window.i18next.t('pmwjs_youve_hit_your_cap_service_fees_increased_to_x', {rate: formatFeePercent(raisedFeeRate)});
  }
  return window.i18next.t('pmwjs_your_plan_supports_x_across_your_events', {allowance: getBannerAllowancePhrase(planLimit)});
};

/**
 * Open the plan chooser from a plan-allowance surface. Shared so every call site attributes the upgrade to the
 * same feature name — that string is the GA4 attribution key and the reason Premium Plus is the highlighted
 * card, so two call sites picking it independently is how they drift apart.
 */
export const openPlanLimitUpgradeDialog = (): void => {
  openPremiumPricingModal(false, PMW_FEATURE_CONSTANTS.TICKETING_PLAN_ALLOWANCE);
};

/** The account-wide figure behind a warned stat card, for its tooltip. Null when that metric has no allowance. */
export const getPlanLimitTicketsTooltip = (planLimit: TicketingPlanLimit): string | null => {
  if (planLimit.thresholdTickets === null) {
    return null;
  }
  return window.i18next.t('pmwjs_youve_processed_x_of_y_tickets_across_your_events', {
    sold: formatNumber(planLimit.ticketsSold),
    total: formatNumber(planLimit.thresholdTickets),
  });
};

export const getPlanLimitSalesTooltip = (planLimit: TicketingPlanLimit): string | null => {
  if (planLimit.thresholdGross === null) {
    return null;
  }
  return window.i18next.t('pmwjs_youve_processed_x_of_y_in_sales_across_your_events', {
    amount: formatNumberInUSD(planLimit.grossSales),
    total: formatWholeNumberInUSD(planLimit.thresholdGross),
  });
};

/**
 * "$2,500 or 100 tickets" for the wizard, "100 tickets or $2,500 sales" for the banner — design words the two
 * surfaces in opposite orders, so each gets its own phrase key rather than one sentence borrowing the other's.
 *
 * Naming both allowances in one phrase keeps a surface saying one thing even though either metric alone can
 * trip. A plan declaring only one names only that one; every tier today declares both, but the wire shape
 * allows either to be absent and a half-written sentence is worse than a branch nobody currently reaches.
 * Thresholds are whole dollars, so they are shown without cents.
 */
const getPlanAllowancePhrase = (planLimit: TicketingPlanLimit): string => {
  return buildAllowancePhrase(planLimit, 'pmwjs_ticketing_allowance_sales_or_tickets');
};

const getBannerAllowancePhrase = (planLimit: TicketingPlanLimit): string => {
  return buildAllowancePhrase(planLimit, 'pmwjs_ticketing_allowance_tickets_or_sales');
};

const buildAllowancePhrase = (planLimit: TicketingPlanLimit, bothPresentKey: string): string => {
  const {thresholdTickets, thresholdGross} = planLimit;

  if (thresholdTickets !== null && thresholdGross !== null) {
    return window.i18next.t(bothPresentKey, {amount: formatWholeNumberInUSD(thresholdGross), tickets: formatNumber(thresholdTickets)});
  }
  if (thresholdTickets !== null) {
    return window.i18next.t('pmwjs_ticketing_allowance_tickets_only', {tickets: formatNumber(thresholdTickets)});
  }
  return formatWholeNumberInUSD(thresholdGross ?? 0);
};
